/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║                        ASK FINLYTICS AI – v3 Layout                        ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║  Answer beside a pinned citation column. Prompts and history open       ║
 * ║  from the toolbar drawer.                                                  ║
 * ║                                                                            ║
 * ║  ┌──────────────────────────────────────────────┬───────────────┐         ║
 * ║  │  CENTER                                      │  SOURCES      │         ║
 * ║  │  Toolbar (company, notes, mode, citations)   │  ~300 px      │         ║
 * ║  │  Answer                                      │  Citations    │         ║
 * ║  │  Composer                                    │               │         ║
 * ║  └──────────────────────────────────────────────┴───────────────┘         ║
 * ║                                                                            ║
 * ║  Responsive:                                                               ║
 * ║   • lg+ (≥1200px): Center + sources column                                ║
 * ║   • <lg: Center only; history and sources open as drawers                 ║
 * ║                                                                            ║
 * ║  Auth Debug Panel (global, in App.js):                                     ║
 * ║   Only renders when process.env.NODE_ENV === 'development'                ║
 * ║   AND URL includes ?debugAuth=1. Guard added in AuthDebugPanel.js.        ║
 * ║                                                                            ║
 * ║  No routes, backend APIs, or data models are changed.                      ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Paper,
    TextField,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Divider,
    Drawer,
    useMediaQuery,
} from '@mui/material';
import {
    AutoAwesome,
    ExpandMore,
    Psychology,
    Speed,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    ArrowUpward,
    Language,
    Storage,
    TravelExplore,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { presentAskAiConfidence } from './askAi/askAiConfidence';
import { api } from '../auth/apiClient';

/* Sub-components */
import AnswerToolbar from './askAi/AnswerToolbar';
import LeftRail from './askAi/LeftRail';
import RightRail from './askAi/RightRail';
import SourcePreviewModal from './askAi/SourcePreviewModal';
import PriceMovePanel from './askAi/PriceMovePanel';
import NotesWindow from './notes/NotesWindow';
import SaveToTabMenu from './notes/SaveToTabMenu';
import useCompanyNotes from '../hooks/useCompanyNotes';
import useTabAutoSave from '../hooks/useTabAutoSave';

const GENERATE_TIMEOUT_MS = 180000;

/* ── Rail widths ── */
const LEFT_RAIL_W = 300;
const RIGHT_RAIL_W = 300;

/* ─────────── Markdown styling (compact-aware, dark theme) ─────────── */
const buildMarkdownStyles = (compact) => ({
    '& h2': {
        fontSize: compact ? '0.98rem' : '1.05rem',
        fontWeight: 700,
        color: 'var(--text-1)',
        mt: compact ? 1.5 : 2.5,
        mb: compact ? 0.5 : 1,
        lineHeight: 1.4,
        '&:first-of-type': { mt: compact ? 0.5 : 1 },
    },
    '& h3': {
        fontSize: compact ? '0.9rem' : '0.95rem',
        fontWeight: 600,
        color: 'var(--text-1)',
        mt: compact ? 1.25 : 2,
        mb: compact ? 0.4 : 0.75,
        lineHeight: 1.4,
    },
    '& p': {
        fontSize: compact ? '0.85rem' : '0.9rem',
        lineHeight: compact ? 1.6 : 1.75,
        color: 'var(--text-2)',
        mb: compact ? 0.5 : 1,
        mt: 0,
    },
    '& ul, & ol': {
        pl: 2.5,
        mb: compact ? 1 : 1.5,
        mt: 0.5,
        '& li': {
            fontSize: compact ? '0.85rem' : '0.9rem',
            lineHeight: compact ? 1.6 : 1.75,
            color: 'var(--text-2)',
            mb: compact ? 0.4 : 0.75,
            '&::marker': { color: 'var(--muted)' },
        },
    },
    '& strong': { fontWeight: 600, color: 'var(--text-1)' },
    '& em': { fontStyle: 'italic', color: 'var(--text-2)' },
    '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        my: 1.5,
        fontSize: '0.85rem',
        '& th': {
            backgroundColor: 'var(--bg-3)',
            fontWeight: 600,
            textAlign: 'left',
            p: 1,
            borderBottom: '2px solid var(--border-light)',
            color: 'var(--text-1)',
        },
        '& td': { p: 1, borderBottom: '1px solid var(--border)', color: 'var(--text-2)' },
        '& tr:hover td': { backgroundColor: 'var(--row-hover)' },
    },
    '& blockquote': {
        borderLeft: '3px solid var(--brand-2)',
        pl: 2,
        py: 0.5,
        my: 1.5,
        backgroundColor: 'var(--bg-3)',
        borderRadius: '0 4px 4px 0',
        '& p': { color: 'var(--text-2)', mb: 0 },
    },
    '& code': {
        backgroundColor: 'var(--bg-3)',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.82rem',
        fontFamily: '"Fira Code", "Consolas", monospace',
        color: 'var(--text-1)',
    },
    '& hr': { border: 'none', borderTop: '1px solid var(--border)', my: 2 },
    '& a': { color: 'var(--brand-2)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
});

/**
 * AskFinlyticsChat – answer pane beside a pinned citation column.
 *
 * Props unchanged: companyId, companyName, availableYears.
 * All backend calls, payloads, state management preserved.
 */
const AskFinlyticsChat = ({ companyId, companyName, availableYears = [] }) => {
    /* ── Notes integration (company-tabbed notebook) ── */
    const [notesOpen, setNotesOpen] = useState(false);
    const companyNotes = useCompanyNotes(companyId, companyName);
    const tabAutoSave = useTabAutoSave(companyId, companyName);
    const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);

    /* ── Existing state (unchanged) ── */
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYears, setSelectedYears] = useState([]);
    const [documentTypes, setDocumentTypes] = useState(['annual_report', 'earnings_call']);
    const [useMultiHop, setUseMultiHop] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [modePreference, setModePreference] = useState('Hybrid');
    const [useAskAiV2, setUseAskAiV2] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    /* ── New layout state ── */
    const [compactMode, setCompactMode] = useState(false);
    const [inlineCitations, setInlineCitations] = useState(true);
    const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
    const [previewSource, setPreviewSource] = useState(null);

    /* ── Responsive breakpoints ── */
    const isLg = useMediaQuery('(min-width:1200px)');
    const showLeftInline = false;
    const showRightInline = isLg;

    /* ── Derived data ── */
    const recentQuestions = useMemo(
        () => messages.filter((m) => m.type === 'user').map((m) => m.content).reverse(),
        [messages],
    );

    const lastAssistantMessage = useMemo(
        () => [...messages].reverse().find((m) => m.type === 'assistant') || null,
        [messages],
    );

    const markdownStyles = useMemo(() => buildMarkdownStyles(compactMode), [compactMode]);

    /* ── Scroll ── */
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);
    useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

    /* ── Load server-side thread on company change ── */
    useEffect(() => {
        setMessages([]);
        if (!companyId) return;
        let cancelled = false;
        api.get('/chat/thread', { params: { companyId } })
            .then((body) => {
                if (cancelled) return;
                const thread = body?.data;
                if (!thread?.messages?.length) return;
                // Reconstruct display messages from stored turns
                const restored = thread.messages
                    .filter((m) => m.role === 'user' || m.role === 'assistant')
                    .map((m, i) => ({
                        id: i,
                        type: m.role === 'user' ? 'user' : 'assistant',
                        content: m.content,
                        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
                        restored: true,
                    }));
                if (restored.length > 0) setMessages(restored);
            })
            .catch(() => { /* thread fetch failure is non-fatal */ });
        return () => { cancelled = true; };
    }, [companyId]);

    /* ── Clear thread (server + local) ── */
    const handleClearThread = useCallback(async () => {
        setMessages([]);
        try {
            await api.delete('/chat/thread', { params: { companyId } });
        } catch (e) {
            // Non-fatal
        }
    }, [companyId]);

    /* ── Conversation history builder (unchanged) ── */
    const buildConversationHistory = useCallback(() => {
        return messages
            .slice(-6)
            .filter((msg) => msg.type === 'user' || msg.type === 'assistant')
            .map((msg) => ({ role: msg.type === 'user' ? 'user' : 'assistant', content: msg.content }));
    }, [messages]);

    /* ══════════════════════════════════════════════════════════════════
       SEND MESSAGE – all API calls unchanged
       ══════════════════════════════════════════════════════════════════ */
    const handleSendMessage = useCallback(
        async (messageOverride) => {
            const messageText = messageOverride || inputValue.trim();
            if (!messageText || isLoading) return;

            const userMessage = {
                id: Date.now(),
                type: 'user',
                content: messageText,
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setInputValue('');
            setIsLoading(true);

            const conversationHistory = buildConversationHistory();

            try {
                let data;
                if (useAskAiV2) {
                    const body = await api.post('/ask-ai', {
                        question: userMessage.content,
                        companyId,
                        companyName,
                        modePreference,
                        fiscalYears: selectedYears.length > 0 ? selectedYears : undefined,
                        documentTypes: documentTypes.length > 0 ? documentTypes : undefined,
                    }, { timeout: GENERATE_TIMEOUT_MS });
                    data = body?.data || body;
                } else {
                    const body = await api.post('/rag/chat', {
                        query: userMessage.content,
                        companyId,
                        companyName,
                        fiscalYears: selectedYears.length > 0 ? selectedYears : undefined,
                        documentTypes: documentTypes.length > 0 ? documentTypes : undefined,
                        useCache: true,
                        multiHop: useMultiHop,
                        conversationHistory: conversationHistory.length > 0 ? conversationHistory : undefined,
                    }, { timeout: GENERATE_TIMEOUT_MS });
                    data = body?.data || body;
                }

                const isV2 = useAskAiV2;
                const presented = isV2 ? presentAskAiConfidence(data.confidence) : null;
                const confidenceLevel = presented ? presented.label : null;
                const confidenceVal = presented ? presented.score : data.confidence;

                const assistantMessage = {
                    id: Date.now() + 1,
                    type: 'assistant',
                    content: data.answer || 'No answer generated.',
                    confidence: confidenceVal,
                    confidenceLevel,
                    keyPoints: data.key_points || data.keyPoints || [],
                    citations: isV2 ? [] : (data.citations || []),
                    sources: isV2 ? (data.sources || []) : [],
                    priceMove: data.priceMove || null,
                    modeUsed: data.modeUsed || null,
                    limitations: data.limitations,
                    strategy: data.strategy,
                    cacheHit: data.cache_hit || data.cacheHit,
                    latencyMs: data.latency_ms || data.latencyMs || data.latencyMs,
                    subAnswers: data.sub_answers || data.subAnswers,
                    internalSourceCount: data.internalSourceCount || 0,
                    webSourceCount: data.webSourceCount || 0,
                    selfCheck: data.selfCheck || null,
                    timestamp: new Date(),
                };

                setMessages((prev) => [...prev, assistantMessage]);

                if (data.cache_hit) toast.info('Answer served from cache', { autoClose: 2000 });
                if (isV2 && data.modeUsed === 'WEB') toast.info('Answered using web sources', { autoClose: 2500 });
                else if (isV2 && data.modeUsed === 'HYBRID') toast.info('Combined internal + web sources', { autoClose: 2500 });
            } catch (error) {
                console.error('RAG chat error:', error);
                setMessages((prev) => [
                    ...prev,
                    {
                        id: Date.now() + 1,
                        type: 'error',
                        content: error.response?.data?.message || 'Failed to get response. Please try again.',
                        timestamp: new Date(),
                    },
                ]);
                toast.error('Failed to get AI response');
            } finally {
                setIsLoading(false);
            }
        },
        [inputValue, isLoading, companyId, companyName, modePreference, selectedYears, documentTypes, useMultiHop, useAskAiV2, buildConversationHistory],
    );

    /* ── Key handlers ── */
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard', { autoClose: 1500 });
    };

    const getConfidenceColor = (c) => (c >= 0.7 ? 'success' : c >= 0.55 ? 'warning' : 'error');
    const getConfidenceLabel = (c) => (c >= 0.7 ? 'High' : c >= 0.55 ? 'Medium' : 'Low');

    /* ── Regenerate ── */
    const handleRegenerate = useCallback(() => {
        const lastUserMsg = [...messages].reverse().find((m) => m.type === 'user');
        if (lastUserMsg) handleSendMessage(lastUserMsg.content);
    }, [messages, handleSendMessage]);

    /* ── Copy last answer ── */
    const handleCopyAnswer = useCallback(() => {
        if (lastAssistantMessage) copyToClipboard(lastAssistantMessage.content);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastAssistantMessage]);

    /* ── Save to Tab (opens tab picker menu) ── */
    const handleSaveToNotesClick = useCallback((event) => {
        if (!lastAssistantMessage) return;
        setSaveMenuAnchor(event.currentTarget);
    }, [lastAssistantMessage]);

    const handleSaveToTab = useCallback(async (tabId) => {
        if (!lastAssistantMessage) return;
        const lastUserMsg = [...messages].reverse().find((m) => m.type === 'user');
        await companyNotes.saveAIResponse(tabId, lastAssistantMessage.content, {
            query: lastUserMsg?.content || 'AI Query',
            companyId,
            companyName,
            mode: modePreference,
        });
    }, [lastAssistantMessage, messages, companyId, companyName, modePreference, companyNotes]);

    /* ── Ctrl+N to toggle notes window ── */
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
                e.preventDefault();
                setNotesOpen(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    /* ── Toggle inline citations ── */
    const processAnswerForDisplay = useCallback(
        (text) => (inlineCitations ? text : text.replace(/\[(?:I|W)\d+\]/g, '')),
        [inlineCitations],
    );

    /* ═══════════════════════════════════════════════════════════════
       Render helpers
       ═══════════════════════════════════════════════════════════════ */

    const renderUserMessage = (message) => (
        <Box sx={{ mb: 1, mt: 3, '&:first-of-type': { mt: 0 } }}>
            <Typography
                variant="h6"
                sx={{
                    fontSize: '1.12rem',
                    fontWeight: 600,
                    color: 'var(--text-1)',
                    lineHeight: 1.5,
                    py: 1,
                    borderBottom: '1px solid var(--border)',
                    mb: 1,
                }}
            >
                {message.content}
            </Typography>
        </Box>
    );

    const renderAssistantMessage = (message) => (
        <Box sx={{ mb: 3 }}>
            {/* Compact metadata row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                {message.modeUsed && (
                    <Chip
                        size="small"
                        icon={
                            message.modeUsed === 'INTERNAL' ? <Storage sx={{ fontSize: 14 }} /> :
                                message.modeUsed === 'WEB' ? <Language sx={{ fontSize: 14 }} /> :
                                    <TravelExplore sx={{ fontSize: 14 }} />
                        }
                        label={message.modeUsed}
                        variant="outlined"
                        sx={{
                            height: 22, fontSize: '0.68rem',
                            borderColor: message.modeUsed === 'WEB' ? 'var(--warning)' : message.modeUsed === 'HYBRID' ? 'var(--brand-2)' : 'var(--brand-2)',
                            color: message.modeUsed === 'WEB' ? 'var(--warning)' : message.modeUsed === 'HYBRID' ? 'var(--brand-2)' : 'var(--brand-2)',
                        }}
                    />
                )}
                {message.confidence !== undefined && (
                    <Chip
                        size="small"
                        icon={message.confidence >= 0.7 ? <CheckCircle sx={{ fontSize: 13 }} /> : message.confidence >= 0.55 ? <Warning sx={{ fontSize: 13 }} /> : <ErrorIcon sx={{ fontSize: 13 }} />}
                        label={`${(message.confidence * 100).toFixed(0)}% ${message.confidenceLevel || getConfidenceLabel(message.confidence)}`}
                        color={getConfidenceColor(message.confidence)}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.68rem' }}
                    />
                )}
                {message.cacheHit && (
                    <Chip size="small" icon={<Speed sx={{ fontSize: 13 }} />} label="Cached" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
                )}
                {message.strategy === 'multi_hop' && (
                    <Chip size="small" icon={<Psychology sx={{ fontSize: 13 }} />} label="Multi-hop" color="secondary" variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
                )}
                {/* Source count chip – opens right drawer on small screens */}
                {(message.sources?.length > 0 || message.citations?.length > 0) && (
                    <Chip
                        size="small"
                        label={
                            message.sources?.length > 0
                                ? `${message.sources.filter((s) => s.sourceType === 'internal').length}I + ${message.sources.filter((s) => s.sourceType === 'web').length}W sources`
                                : `${message.citations?.length || 0} sources`
                        }
                        variant="outlined"
                        clickable
                        onClick={() => !showRightInline && setRightDrawerOpen(true)}
                        sx={{ height: 22, fontSize: '0.68rem', borderColor: 'var(--border)', color: 'var(--text-2)' }}
                    />
                )}
                {message.latencyMs && (
                    <Typography variant="caption" sx={{ color: 'var(--muted)', fontSize: '0.68rem' }}>
                        {(message.latencyMs / 1000).toFixed(1)}s
                    </Typography>
                )}
                {message.priceMove && (
                    <Chip
                        size="small"
                        label="Price move"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.68rem', borderColor: 'var(--brand-2)', color: 'var(--brand-2)' }}
                    />
                )}
            </Box>

            {message.priceMove && <PriceMovePanel priceMove={message.priceMove} />}

            {/* Main markdown answer */}
            <Box sx={{ ...markdownStyles }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {processAnswerForDisplay(message.content)}
                </ReactMarkdown>
            </Box>

            {/* Sub-answers */}
            {message.subAnswers && message.subAnswers.length > 0 && (
                <Accordion
                    sx={{
                        mt: 2,
                        backgroundColor: 'var(--bg-3)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-2)', fontWeight: 500 }}>
                            Reasoning breakdown ({message.subAnswers.length} sub-questions)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        {message.subAnswers.map((sub, idx) => (
                            <Box key={idx} sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid var(--brand-2)' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--brand-2)' }}>
                                    Q{idx + 1}: {sub.question}
                                </Typography>
                                <Box sx={{ ...markdownStyles, '& p': { fontSize: '0.82rem', color: 'var(--text-2)' } }}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{sub.answer}</ReactMarkdown>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );

    const renderErrorMessage = (message) => (
        <Box sx={{ mb: 2 }}><Alert severity="error" sx={{ borderRadius: 2 }}>{message.content}</Alert></Box>
    );

    const suggestedQuestions = useMemo(() => [
        `What was ${companyName || companyId}'s revenue growth?`,
        'Explain the key risk factors',
        'What are the main business segments?',
        'How has the profit margin changed?',
        'What does management say about future outlook?',
    ], [companyName, companyId]);

    /* ── Filter bar ── */
    const FilterBar = () => (
        <Box
            sx={{
                px: { xs: 2, md: 3 },
                py: 1.25,
                borderBottom: '1px solid var(--border)',
                backgroundColor: 'var(--bg-2)',
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
            }}
        >
            <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Fiscal Years</InputLabel>
                <Select
                    multiple value={selectedYears}
                    onChange={(e) => setSelectedYears(e.target.value)}
                    label="Fiscal Years"
                    renderValue={(sel) => (sel.length > 0 ? `${sel.length} selected` : 'All')}
                    sx={{ fontSize: '0.83rem' }}
                >
                    {(availableYears.length > 0 ? availableYears : [2024, 2023, 2022, 2021, 2020]).map((yr) => (
                        <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Document Types</InputLabel>
                <Select
                    multiple value={documentTypes}
                    onChange={(e) => setDocumentTypes(e.target.value)}
                    label="Document Types"
                    renderValue={(sel) => (sel.length > 0 ? `${sel.length} selected` : 'All')}
                    sx={{ fontSize: '0.83rem' }}
                >
                    <MenuItem value="annual_report">Annual Reports</MenuItem>
                    <MenuItem value="earnings_call">Earnings Calls</MenuItem>
                </Select>
            </FormControl>
            <FormControlLabel
                control={<Switch checked={useMultiHop} onChange={(e) => setUseMultiHop(e.target.checked)} size="small" />}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Psychology sx={{ fontSize: 16 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Multi-hop</Typography>
                    </Box>
                }
            />
        </Box>
    );

    /* ═══════════════════════════════════════════════════════════════
       MAIN RENDER – answer plus citation column
       ═══════════════════════════════════════════════════════════════ */
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row', backgroundColor: 'var(--bg-1)', overflow: 'hidden' }}>

            {/* ═══════════ LEFT RAIL ═══════════ */}
            {showLeftInline ? (
                <Box sx={{ width: LEFT_RAIL_W, minWidth: LEFT_RAIL_W, height: '100%' }}>
                    <LeftRail
                        onSelectQuestion={(q) => handleSendMessage(q)}
                        onNewQuestion={() => { setInputValue(''); inputRef.current?.focus(); }}
                        onClearThread={handleClearThread}
                        recentQuestions={recentQuestions}
                        companyName={companyName}
                        isMobile={false}
                    />
                </Box>
            ) : (
                <Drawer
                    anchor="left"
                    open={leftDrawerOpen}
                    onClose={() => setLeftDrawerOpen(false)}
                    PaperProps={{ sx: { width: LEFT_RAIL_W } }}
                >
                    <LeftRail
                        onSelectQuestion={(q) => { handleSendMessage(q); setLeftDrawerOpen(false); }}
                        onNewQuestion={() => { setInputValue(''); setLeftDrawerOpen(false); inputRef.current?.focus(); }}
                        onClearThread={() => { handleClearThread(); setLeftDrawerOpen(false); }}
                        recentQuestions={recentQuestions}
                        companyName={companyName}
                        isMobile
                        onClose={() => setLeftDrawerOpen(false)}
                    />
                </Drawer>
            )}

            {/* ═══════════ CENTER MAIN PANE ═══════════ */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <AnswerToolbar
                    modePreference={modePreference}
                    onModeChange={setModePreference}
                    inlineCitations={inlineCitations}
                    onToggleInlineCitations={() => setInlineCitations((p) => !p)}
                    compactMode={compactMode}
                    onToggleCompact={() => setCompactMode((p) => !p)}
                    onRegenerate={handleRegenerate}
                    onCopyAnswer={handleCopyAnswer}
                    onSaveToNotes={handleSaveToNotesClick}
                    hasAnswer={!!lastAssistantMessage}
                    onOpenHistory={() => setLeftDrawerOpen(true)}
                    onOpenSources={() => setRightDrawerOpen(true)}
                    showHistoryBtn={!showLeftInline}
                    showSourcesBtn={!showRightInline}
                    onToggleFilters={() => setShowFilters((p) => !p)}
                    showFilters={showFilters}
                    companyLabel={companyName || companyId}
                    notesBadge={companyNotes.badge}
                    onOpenNotes={() => setNotesOpen(true)}
                    useAskAiV2={useAskAiV2}
                    onToggleAskAiV2={() => setUseAskAiV2((prev) => !prev)}
                />

                {showFilters && <FilterBar />}

                {/* ── Scrollable answer area (FULL WIDTH) ── */}
                <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--bg-1)' }}>
                    {messages.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', px: 3 }}>
                            <AutoAwesome sx={{ fontSize: 44, color: 'var(--muted)', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-1)', mb: 0.5 }}>
                                Ask questions about {companyName || companyId}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'var(--text-2)', mb: 4, textAlign: 'center', maxWidth: 560 }}>
                                I can answer questions based on uploaded annual reports and earnings call transcripts.
                                If internal sources don't have the answer, I'll search the web for you.
                            </Typography>
                            <Divider sx={{ width: '100%', maxWidth: 650, mb: 3 }}>
                                <Chip label="Try these questions" size="small" sx={{ fontSize: '0.73rem', color: 'var(--muted)' }} />
                            </Divider>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: 750 }}>
                                {suggestedQuestions.map((q, idx) => (
                                    <Chip
                                        key={idx}
                                        label={q}
                                        variant="outlined"
                                        clickable
                                        onClick={() => handleSendMessage(q)}
                                        sx={{
                                            fontSize: '0.82rem', borderColor: 'var(--border)', color: 'var(--text-2)',
                                            '&:hover': { borderColor: 'var(--brand-2)', backgroundColor: 'var(--bg-3)', color: 'var(--brand-2)' },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        /* ── Conversation thread — NO max-w constraint ── */
                        <Box sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 }, py: 2 }}>
                            {messages.map((message) => {
                                switch (message.type) {
                                    case 'user': return <Box key={message.id}>{renderUserMessage(message)}</Box>;
                                    case 'assistant': return <Box key={message.id}>{renderAssistantMessage(message)}</Box>;
                                    case 'error': return <Box key={message.id}>{renderErrorMessage(message)}</Box>;
                                    default: return null;
                                }
                            })}

                            {isLoading && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: '50%', backgroundColor: 'var(--bg-3)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <CircularProgress size={16} sx={{ color: 'var(--brand-2)' }} />
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'var(--text-2)' }}>
                                            {modePreference === 'WebOnly' ? 'Searching the web...' : 'Analyzing documents...'}
                                        </Typography>
                                    </Box>
                                    {[90, 100, 75, 85, 60].map((w, i) => (
                                        <Box key={i} sx={{
                                            height: 12, width: `${w}%`, backgroundColor: 'var(--bg-3)', borderRadius: 1, mb: 1,
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            '@keyframes pulse': { '0%, 100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
                                        }} />
                                    ))}
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>
                    )}
                </Box>

                {/* ── Fixed input bar ── */}
                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.25, borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-2)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip size="small" label={modePreference} variant="outlined" sx={{ height: 18, fontSize: '0.6rem', borderColor: 'var(--border)', color: 'var(--muted)' }} />
                        {lastAssistantMessage?.confidence != null && (
                            <Chip size="small" label={`${(lastAssistantMessage.confidence * 100).toFixed(0)}% conf`} variant="outlined" sx={{ height: 18, fontSize: '0.6rem', borderColor: 'var(--border)', color: 'var(--muted)' }} />
                        )}
                    </Box>
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex', alignItems: 'flex-end', gap: 1,
                            border: '1px solid var(--border)', borderRadius: 3, px: 2, py: 0.75,
                            transition: 'border-color 0.2s', backgroundColor: 'var(--bg-3)',
                            '&:focus-within': { borderColor: 'var(--brand-2)', boxShadow: '0 0 0 2px rgba(42,143,255,0.1)' },
                        }}
                    >
                        <TextField
                            fullWidth multiline maxRows={5}
                            placeholder={`Ask about ${companyName || companyId}...`}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: '0.9rem',
                                    py: 0.5,
                                    color: 'var(--text-1)',
                                    '& input': { color: 'var(--text-1)' },
                                    '& textarea': { color: 'var(--text-1)' },
                                    '&::placeholder': { color: 'var(--muted)', opacity: 0.7 }
                                }
                            }}
                            inputRef={inputRef}
                        />
                        <IconButton
                            onClick={() => handleSendMessage()}
                            disabled={!inputValue.trim() || isLoading}
                            sx={{
                                backgroundColor: inputValue.trim() && !isLoading ? 'var(--brand-2)' : 'var(--bg-3)',
                                color: inputValue.trim() && !isLoading ? 'var(--text-1)' : 'var(--muted)',
                                width: 34, height: 34, mb: 0.25,
                                '&:hover': { backgroundColor: inputValue.trim() && !isLoading ? 'var(--brand-2)' : 'var(--bg-3)' },
                                transition: 'all 0.2s',
                            }}
                        >
                            {isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowUpward sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Paper>
                    <Typography variant="caption" sx={{ color: 'var(--muted)', mt: 0.5, display: 'block', textAlign: 'center', fontSize: '0.68rem' }}>
                        Enter to send · Shift+Enter for new line
                    </Typography>
                </Box>
            </Box>

            {/* ═══════════ RIGHT RAIL ═══════════ */}
            {showRightInline ? (
                <Box sx={{ width: RIGHT_RAIL_W, minWidth: RIGHT_RAIL_W, height: '100%' }}>
                    <RightRail
                        lastMessage={lastAssistantMessage}
                        isLoading={isLoading}
                        onSourceClick={(s) => setPreviewSource(s)}
                        isMobile={false}
                    />
                </Box>
            ) : (
                <Drawer
                    anchor="right"
                    open={rightDrawerOpen}
                    onClose={() => setRightDrawerOpen(false)}
                    PaperProps={{ sx: { width: Math.min(RIGHT_RAIL_W, 360) } }}
                >
                    <RightRail
                        lastMessage={lastAssistantMessage}
                        isLoading={isLoading}
                        onSourceClick={(s) => { setPreviewSource(s); setRightDrawerOpen(false); }}
                        isMobile
                        onClose={() => setRightDrawerOpen(false)}
                    />
                </Drawer>
            )}

            {/* ═══════════ SOURCE PREVIEW MODAL ═══════════ */}
            <SourcePreviewModal
                open={!!previewSource}
                onClose={() => setPreviewSource(null)}
                source={previewSource}
            />

            {/* ═══════════ SAVE-TO-TAB MENU ═══════════ */}
            <SaveToTabMenu
                anchorEl={saveMenuAnchor}
                open={Boolean(saveMenuAnchor)}
                onClose={() => setSaveMenuAnchor(null)}
                tabs={companyNotes.tabs}
                onSelectTab={handleSaveToTab}
            />

            {/* ═══════════ NOTES WINDOW ═══════════ */}
            <NotesWindow
                isOpen={notesOpen}
                onClose={() => setNotesOpen(false)}
                companyId={companyId}
                companyName={companyName}
                tabs={companyNotes.tabs}
                activeTabId={companyNotes.activeTabId}
                setActiveTabId={companyNotes.setActiveTabId}
                activeTab={companyNotes.activeTab}
                loading={companyNotes.loading}
                fetchNotes={companyNotes.fetchNotes}
                addTab={companyNotes.addTab}
                removeTab={companyNotes.removeTab}
                renameTab={companyNotes.renameTab}
                updateLocalTabContent={companyNotes.updateLocalTabContent}
                saveStatus={tabAutoSave.saveStatus}
                errorMsg={tabAutoSave.errorMsg}
                scheduleSave={tabAutoSave.scheduleSave}
                flushSave={tabAutoSave.flushSave}
                manualSave={tabAutoSave.manualSave}
            />
        </Box>
    );
};

export default AskFinlyticsChat;
